/**
 * Safe Write — Deterministic File-Write Guard
 *
 * The controlled I/O chokepoint for ALL file writes from specialist otters.
 * Every write goes through per-otter path allowlists. The LLM cannot write
 * to arbitrary filesystem paths.
 *
 * Why this matters: defense/regulated industry customers need guarantees that
 * "the LLM cannot decide what to read and write from anywhere on your filesystem."
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { boolCoerce } from '../coerce.js';
import { writeFileSync, readFileSync, existsSync, mkdirSync, lstatSync } from 'fs';
import { normalize, isAbsolute, dirname, join } from 'path';
import { emit } from '@stackwright-pro/telemetry';

// ---------------------------------------------------------------------------
// Per-otter path allowlists
// ---------------------------------------------------------------------------

/**
 * Allowlists define which file patterns each otter is permitted to write.
 * Paths are relative to the project root (cwd).
 * Patterns use simple prefix + suffix matching (no regex — deterministic and auditable).
 */
interface PathAllowRule {
  /** Path prefix the file must start with (relative to project root) */
  prefix: string;
  /** File suffix/extension the file must end with */
  suffix: string;
  /** Human-readable description for audit logs */
  description: string;
}

const OTTER_WRITE_ALLOWLISTS: Record<string, PathAllowRule[]> = {
  'stackwright-pro-designer-otter': [
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'Design language artifact' },
  ],
  'stackwright-pro-theme-otter': [
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'Theme tokens artifact' },
    {
      prefix: 'stackwright.theme.',
      suffix: '.yml',
      description: 'Theme config YAML (merged at build time)',
    },
  ],
  'stackwright-pro-auth-otter': [
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'Auth config artifact' },
    {
      prefix: 'stackwright.auth.',
      suffix: '.yml',
      description: 'Auth config YAML (stackwright.auth.yml — compiled to _auth.json)',
    },
    {
      prefix: '.env',
      suffix: '',
      description: 'Dotenv files (.env, .env.local, .env.production, etc.)',
    },
    {
      prefix: 'lib/mock-auth',
      suffix: '.ts',
      description: 'Mock auth module (DEV_ONLY_MODE role updates)',
    },
    {
      prefix: 'package.json',
      suffix: '.json',
      description: 'Project package.json (DEV_ONLY_MODE script cleanup)',
    },
  ],
  'stackwright-pro-data-otter': [
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'Data config artifact' },
    {
      prefix: 'stackwright.collections.',
      suffix: '.yml',
      description:
        'Collections config YAML (stackwright.collections.yml — compiled to _collections.json)',
    },
  ],
  'stackwright-pro-page-otter': [
    { prefix: 'pages/', suffix: '/content.yml', description: 'Page content YAML' },
    { prefix: 'pages/', suffix: '/content.yaml', description: 'Page content YAML' },
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'Pages manifest' },
  ],
  'stackwright-pro-dashboard-otter': [
    { prefix: 'pages/', suffix: '/content.yml', description: 'Dashboard content YAML' },
    { prefix: 'pages/', suffix: '/content.yaml', description: 'Dashboard content YAML' },
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'Dashboard manifest' },
  ],
  'stackwright-pro-form-wizard-otter': [
    { prefix: 'workflows/', suffix: '.yml', description: 'Workflow definition' },
    { prefix: 'workflows/', suffix: '.yaml', description: 'Workflow definition' },
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'Workflow config' },
  ],
  'stackwright-pro-scaffold-otter': [
    { prefix: 'app/', suffix: '.tsx', description: 'Next.js App Router shell files' },
    {
      prefix: '.stackwright/artifacts/',
      suffix: '.json',
      description: 'Scaffold manifest artifact',
    },
  ],
  'stackwright-pro-api-otter': [
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'API config artifact' },
    {
      prefix: 'stackwright.integrations.',
      suffix: '.yml',
      description:
        'Integrations config YAML (stackwright.integrations.yml — compiled to _integrations.json)',
    },
  ],
  'stackwright-pro-geo-otter': [
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'Geo manifest artifact' },
    { prefix: 'pages/', suffix: '/content.yml', description: 'Map page content' },
    { prefix: 'pages/', suffix: '/content.yaml', description: 'Map page content' },
  ],
  'stackwright-pro-polish-otter': [
    {
      prefix: 'stackwright.yml',
      suffix: '',
      description: 'Stackwright config (navigation updates)',
    },
    {
      prefix: 'stackwright.navigation.',
      suffix: '.yml',
      description: 'Navigation config YAML (merged at build time)',
    },
    { prefix: 'pages/', suffix: '/content.yml', description: 'Landing page content' },
    { prefix: 'pages/', suffix: '/content.yaml', description: 'Landing page content' },
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'Polish artifact' },
    { prefix: 'README.md', suffix: '', description: 'Project README rewrite' },
    {
      prefix: '.env.local',
      suffix: '',
      description:
        '.env.local runtime config — emitter output from stackwright_pro_emit_env_local (swp-8qdd)',
    },
    {
      prefix: '.env.local.example',
      suffix: '',
      description:
        '.env.local.example template — emitter output from stackwright_pro_emit_env_local (swp-8qdd)',
    },
  ],
  // QA otter writes ONLY to the qa/ directory and the qa-findings artifact.
  // It never writes .tsx, .ts, .yml, or .json outside these paths.
  'stackwright-pro-qa-otter': [
    {
      prefix: '.stackwright/artifacts/qa-findings.json',
      suffix: '',
      description: 'QA findings artifact',
    },
    {
      prefix: '.stackwright/qa/',
      suffix: '.md',
      description: 'QA report markdown',
    },
    {
      prefix: '.stackwright/qa/screenshots/',
      suffix: '.png',
      description: 'Route screenshots for evidence',
    },
  ],
  // domain-expert-otter is a reader/answerer only — it writes via stackwright_pro_save_phase_answers,
  // not via safe_write. Entry required here because the MCP tool availability guard boilerplate
  // in its system prompt mentions stackwright_pro_safe_write (triggering the coherence test).
  'stackwright-pro-domain-expert-otter': [],
  'stackwright-services-otter': [
    { prefix: '.stackwright/artifacts/', suffix: '.json', description: 'Services config artifact' },
    { prefix: 'services/', suffix: '.ts', description: 'Service implementation files' },
    { prefix: 'services/', suffix: '.yaml', description: 'Service flow definitions' },
    { prefix: 'services/', suffix: '.yml', description: 'Service flow definitions' },
    { prefix: 'lib/seeds/', suffix: '.ts', description: 'Seed data files' },
    { prefix: 'specs/', suffix: '.json', description: 'Generated OpenAPI specs' },
    { prefix: 'specs/', suffix: '.yaml', description: 'Generated OpenAPI specs' },
    { prefix: 'stackwright-generated/', suffix: '.json', description: 'Services manifest' },
  ],
};

// ---------------------------------------------------------------------------
// Protected paths — managed by other sink tools, never written here
// ---------------------------------------------------------------------------

const PROTECTED_PATH_PREFIXES = [
  '.stackwright/pipeline-state.json',
  '.stackwright/pipeline-keys.json', // ephemeral signing keys
  '.stackwright/artifacts/signatures.json', // artifact signature manifest
  '.stackwright/questions/',
  '.stackwright/answers/',
  '.stackwright/page-registry.json', // page ownership — managed by safe_write internally
];

// ---------------------------------------------------------------------------
// Per-file-type content size limits (defense-in-depth: disk exhaustion vector)
// Mirrors MAX_OTTER_BYTES in integrity.ts — different thresholds per content type.
// Exported for test assertions; callers cannot negotiate around these limits.
// ---------------------------------------------------------------------------

/** 512 KB — JSON artifacts (.stackwright/artifacts/*.json) */
export const MAX_SAFE_WRITE_BYTES_JSON = 512 * 1024;

/** 256 KB — YAML content files (pages, workflows, config) */
export const MAX_SAFE_WRITE_BYTES_YAML = 256 * 1024;

/** 4 KB — dotenv files (.env, .env.local, .env.production) */
export const MAX_SAFE_WRITE_BYTES_ENV = 4 * 1024;

/** 256 KB — fallback for any unrecognized extension */
export const MAX_SAFE_WRITE_BYTES_DEFAULT = 256 * 1024;

/**
 * Resolve the maximum allowed byte size for a given file path.
 * Exported for testing — not for callers to negotiate around.
 */
export function getMaxBytesForPath(filePath: string): { limit: number; label: string } {
  if (filePath.endsWith('.json')) return { limit: MAX_SAFE_WRITE_BYTES_JSON, label: 'JSON' };
  if (filePath.endsWith('.yml') || filePath.endsWith('.yaml'))
    return { limit: MAX_SAFE_WRITE_BYTES_YAML, label: 'YAML' };
  if (filePath === '.env' || /^\.env\.[a-zA-Z0-9]+/.test(filePath))
    return { limit: MAX_SAFE_WRITE_BYTES_ENV, label: 'env' };
  return { limit: MAX_SAFE_WRITE_BYTES_DEFAULT, label: 'default' };
}

// ---------------------------------------------------------------------------
// Page ownership registry — prevents cross-otter page overwrites (swp-73c)
// ---------------------------------------------------------------------------

export interface PageRegistryEntry {
  /** Slug extracted from the page path (e.g. "ship-watch") */
  slug: string;
  /** Otter that first wrote this page */
  claimedBy: string;
  /** Content types found in the page YAML (e.g. ["map_pulse"]) */
  contentTypes: string[];
  /** ISO timestamp of last write */
  writtenAt: string;
}

const PAGE_REGISTRY_FILE = '.stackwright/page-registry.json';

/**
 * Extract the page slug from a file path like "pages/ship-watch/content.yml".
 * Returns null if the path doesn't match the page pattern.
 */
export function extractPageSlug(filePath: string): string | null {
  const match = normalize(filePath).match(/^pages\/(.+?)\/content\.ya?ml$/);
  return match?.[1] ?? null;
}

/**
 * Extract content type values from YAML content using a simple regex.
 * Looks for `- type: <value>` patterns in content_items.
 * Falls back to ['unknown'] if no types are found.
 */
export function extractContentTypes(yamlContent: string): string[] {
  const typePattern = /^\s*-?\s*type:\s*(\S+)/gm;
  const types: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = typePattern.exec(yamlContent)) !== null) {
    const typeName = m[1];
    if (typeName && !types.includes(typeName)) {
      types.push(typeName);
    }
  }
  return types.length > 0 ? types : ['unknown'];
}

/** Read the page registry from disk. Returns empty record if missing. */
export function readPageRegistry(cwd: string): Record<string, PageRegistryEntry> {
  const regPath = join(cwd, PAGE_REGISTRY_FILE);
  if (!existsSync(regPath)) return {};
  try {
    // Reject symlinks (same pattern as pipeline safeReadSync)
    const stat = lstatSync(regPath);
    if (stat.isSymbolicLink()) return {};
    return JSON.parse(readFileSync(regPath, 'utf-8')) as Record<string, PageRegistryEntry>;
  } catch {
    return {};
  }
}

/** Write the page registry to disk. Creates .stackwright/ if needed. */
export function writePageRegistry(cwd: string, registry: Record<string, PageRegistryEntry>): void {
  const dir = join(cwd, '.stackwright');
  mkdirSync(dir, { recursive: true });
  const regPath = join(cwd, PAGE_REGISTRY_FILE);
  // Reject symlinks before writing
  if (existsSync(regPath)) {
    const stat = lstatSync(regPath);
    if (stat.isSymbolicLink()) {
      throw new Error('Refusing to write page registry through symlink');
    }
  }
  writeFileSync(regPath, JSON.stringify(registry, null, 2) + '\n', { encoding: 'utf-8' });
}

/**
 * Check page ownership. Returns null if the write is allowed,
 * or an error object if the slug is owned by a different otter.
 */
export function checkPageOwnership(
  cwd: string,
  slug: string,
  callerOtter: string
): { allowed: true } | { allowed: false; owner: string; contentTypes: string[] } {
  const registry = readPageRegistry(cwd);
  const existing = registry[slug];
  if (!existing || existing.claimedBy === callerOtter) {
    return { allowed: true };
  }
  return {
    allowed: false,
    owner: existing.claimedBy,
    contentTypes: existing.contentTypes,
  };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SafeWriteInput {
  callerOtter: string;
  filePath: string;
  content: string;
  createDirectories?: boolean;
  _cwd?: string;
}

interface SafeWriteSuccess {
  success: true;
  path: string;
  bytesWritten: number;
  allowRule: string;
}

interface SafeWriteRejection {
  success: false;
  error: string;
  callerOtter: string;
  attemptedPath: string;
  allowedPaths: string[];
}

// ---------------------------------------------------------------------------
// Core security logic — exported for testing
// ---------------------------------------------------------------------------

/**
 * Next.js dynamic-segment patterns that are legitimate directory names.
 * Stripped from the path BEFORE the traversal check to avoid false positives
 * (e.g. `[...slug]` contains `..` as a substring but is NOT a traversal).
 *
 * Matches (in priority order — double-bracket first to avoid partial match):
 *   [[...paramName]]  — optional catch-all  e.g. `[[...slug]]`
 *   [...paramName]    — catch-all           e.g. `[...rest]`
 *   [paramName]       — dynamic segment     e.g. `[id]`
 *
 * param names must be `[a-zA-Z_][a-zA-Z0-9_]*` — no dots, no slashes,
 * so a forged segment like `[...../../etc]` is NOT stripped and will be
 * caught by the traversal check after `normalize()` resolves the `..` parts.
 */
const NEXTJS_BRACKET_SEGMENT_RE =
  /\[\[\.{3}[a-zA-Z_][a-zA-Z0-9_]*\]\]|\[\.{3}[a-zA-Z_][a-zA-Z0-9_]*\]|\[[a-zA-Z_][a-zA-Z0-9_]*\]/g;

/**
 * Remove well-formed Next.js bracket segments from a normalized path so the
 * traversal check that follows doesn't false-positive on `...` in `[...slug]`.
 */
function stripNextjsBracketSegments(path: string): string {
  return path.replace(NEXTJS_BRACKET_SEGMENT_RE, '');
}

export function checkPathAllowed(
  callerOtter: string,
  filePath: string
): { allowed: boolean; rule?: string; error?: string } {
  // 1. Normalize the path — reject path traversal.
  //    Strip legitimate Next.js bracket segments first so `[...slug]` (which
  //    contains the substring `..`) doesn't trigger a false positive.
  //    Forged patterns like `[...../../etc]` are NOT stripped (param names
  //    cannot contain dots/slashes) so real traversal is still caught.
  const normalized = normalize(filePath);
  if (stripNextjsBracketSegments(normalized).includes('..')) {
    return { allowed: false, error: 'Path traversal detected: ".." segments are not allowed' };
  }

  // 2. Reject absolute paths
  if (isAbsolute(normalized)) {
    return {
      allowed: false,
      error: 'Absolute paths are not allowed — use paths relative to project root',
    };
  }

  // 3. Foreman never writes directly
  if (callerOtter === 'stackwright-pro-foreman-otter') {
    return {
      allowed: false,
      error: 'The foreman otter coordinates — it does not write files directly',
    };
  }

  // 4. Check caller is known
  const allowlist = OTTER_WRITE_ALLOWLISTS[callerOtter];
  if (!allowlist) {
    return {
      allowed: false,
      error: `Unknown otter: "${callerOtter}" is not in the write allowlist`,
    };
  }

  // 5. Reject protected paths (managed by other sink tools)
  for (const protectedPrefix of PROTECTED_PATH_PREFIXES) {
    if (normalized === protectedPrefix || normalized.startsWith(protectedPrefix)) {
      return {
        allowed: false,
        error: `Path "${normalized}" is managed by dedicated sink tools, not safe_write`,
      };
    }
  }

  // 6. Check path matches at least one allowlist rule
  for (const rule of allowlist) {
    const prefixMatch = normalized.startsWith(rule.prefix);
    const suffixMatch = rule.suffix === '' || normalized.endsWith(rule.suffix);
    if (prefixMatch && suffixMatch) {
      // For .env rules, enforce strict dotenv filename pattern
      // Only allow: .env, .env.local, .env.development, .env.production, .env.test, etc.
      // Reject: .envrc, .env.sh, .environment/, .envoy.yaml
      if (rule.prefix === '.env' && rule.suffix === '') {
        if (!/^\.env(\.[a-zA-Z0-9]{3,})*$/.test(normalized)) {
          continue; // this rule doesn't match, try next
        }
      }
      // For lib/mock-auth rule, enforce exact path match
      // Rejects: lib/mock-auth-utils.ts, lib/mock-auth-helper.ts, etc.
      if (rule.prefix === 'lib/mock-auth' && rule.suffix === '.ts') {
        if (normalized !== 'lib/mock-auth.ts') {
          continue; // this rule doesn't match, try next
        }
      }
      // For package.json rule, enforce exact path match
      // Rejects: packages/sub/package.json, package.json.backup, etc.
      if (rule.prefix === 'package.json' && rule.suffix === '.json') {
        if (normalized !== 'package.json') {
          continue; // this rule doesn't match, try next
        }
      }
      return { allowed: true, rule: rule.description };
    }
  }

  return {
    allowed: false,
    error: `Path "${normalized}" does not match any allowed write pattern for ${callerOtter}`,
  };
}

// ---------------------------------------------------------------------------
// Content validation helpers
// ---------------------------------------------------------------------------

function validateJsonContent(content: string): string | null {
  try {
    JSON.parse(content);
    return null;
  } catch (err) {
    return `Invalid JSON: ${err instanceof Error ? err.message : String(err)}`;
  }
}

function validateYamlContent(content: string): string | null {
  const trimmed = content.trimStart();

  // Entity expansion DoS check — reject deeply nested anchor references
  // Pattern: &anchor followed by [*anchor] repeated many times
  // 20+ occurrences of any [*name] pattern indicates potential entity expansion attack
  const anchorRefPattern = /\[(\*[\w]+)\]/g;
  const matches = [...content.matchAll(anchorRefPattern)];
  if (matches.length >= 20) {
    return 'YAML entity expansion pattern detected — too many anchor references';
  }

  // Also check for repeated identical anchor refs (exponential expansion)
  const anchorDefPattern = /&([\w]+)/g;
  const defs = [...content.matchAll(anchorDefPattern)];
  if (defs.length >= 10) {
    // Check for exponential pattern: &a [*a *a *a...]
    const anchorNameCounts = new Map<string, number>();
    for (const [, name] of defs) {
      const count = (anchorNameCounts.get(name) ?? 0) + 1;
      anchorNameCounts.set(name, count);
      if (count >= 10) {
        return 'YAML entity expansion: repeated anchor definitions detected';
      }
    }
  }

  if (trimmed.startsWith('import ') || trimmed.startsWith('import{')) {
    return 'Content starts with "import" — this looks like code, not YAML';
  }
  if (trimmed.startsWith('export ') || trimmed.startsWith('export{')) {
    return 'Content starts with "export" — this looks like code, not YAML';
  }
  if (trimmed.startsWith('#!'))
    return 'Content starts with shebang — this looks like a script, not YAML';
  if (/!!(?:python|ruby|perl|js|java)/i.test(content))
    return 'YAML deserialization attack tags detected';
  if (trimmed.startsWith('<') && !trimmed.startsWith('<<'))
    return 'Content looks like markup, not YAML';
  return null;
}

function validateEnvContent(content: string): string | null {
  const lines = content.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    if (raw === undefined) continue;
    const line = raw.trim();
    // Allow empty lines, comments, and key=value pairs
    if (line === '' || line.startsWith('#')) continue;
    if (!/^[A-Za-z_][A-Za-z0-9_]*=/.test(line)) {
      return `Line ${i + 1} is not a valid env entry (expected KEY=value): "${line}"`;
    }
  }
  return null;
}

function validateContent(filePath: string, content: string): string | null {
  if (filePath.endsWith('.json')) return validateJsonContent(content);
  if (filePath.endsWith('.yml') || filePath.endsWith('.yaml')) return validateYamlContent(content);
  if (filePath === '.env' || filePath.startsWith('.env')) return validateEnvContent(content);
  return null;
}

// ---------------------------------------------------------------------------
// Handler (exported for direct testing — no MCP server needed)
// ---------------------------------------------------------------------------

function _safeWriteInner(input: SafeWriteInput): { text: string; isError: boolean } {
  const cwd = input._cwd ?? process.cwd();
  const { callerOtter, filePath, content, createDirectories = true } = input;

  // 1. Path allowlist check
  const check = checkPathAllowed(callerOtter, filePath);
  if (!check.allowed) {
    const allowlist = OTTER_WRITE_ALLOWLISTS[callerOtter] ?? [];
    const allowedPaths = allowlist.map((r) => `${r.prefix}*${r.suffix} (${r.description})`);
    const result: SafeWriteRejection = {
      success: false,
      error: check.error ?? 'Path not allowed',
      callerOtter,
      attemptedPath: filePath,
      allowedPaths,
    };
    return { text: JSON.stringify(result), isError: true };
  }

  // 2. Content size limit check (defense-in-depth: disk exhaustion vector)
  const contentBytes = Buffer.byteLength(content, 'utf-8');
  const { limit: maxBytes, label: fileTypeLabel } = getMaxBytesForPath(filePath);
  if (contentBytes > maxBytes) {
    const result: SafeWriteRejection = {
      success: false,
      error: `Content size ${contentBytes} bytes exceeds ${fileTypeLabel} limit of ${maxBytes} bytes (${maxBytes / 1024} KB)`,
      callerOtter,
      attemptedPath: filePath,
      allowedPaths: [],
    };
    return { text: JSON.stringify(result), isError: true };
  }

  const normalized = normalize(filePath);
  const fullPath = join(cwd, normalized);

  // 3. Reject symlinks at target
  if (existsSync(fullPath)) {
    try {
      const stat = lstatSync(fullPath);
      if (stat.isSymbolicLink()) {
        const result: SafeWriteRejection = {
          success: false,
          error: 'Target path is a symlink — refusing to write through symlinks for security',
          callerOtter,
          attemptedPath: filePath,
          allowedPaths: [],
        };
        return { text: JSON.stringify(result), isError: true };
      }
    } catch {
      // lstat failed — file might have been removed between exists check and lstat, proceed
    }
  }

  // 4. Content validation
  const contentError = validateContent(normalized, content);
  if (contentError) {
    const result: SafeWriteRejection = {
      success: false,
      error: `Content validation failed: ${contentError}`,
      callerOtter,
      attemptedPath: filePath,
      allowedPaths: [],
    };
    return { text: JSON.stringify(result), isError: true };
  }

  // 5. Page ownership check — prevent cross-otter page overwrites (swp-73c)
  const pageSlug = extractPageSlug(normalized);
  if (pageSlug) {
    const ownership = checkPageOwnership(cwd, pageSlug, callerOtter);
    if (!ownership.allowed) {
      const result: SafeWriteRejection = {
        success: false,
        error: `Page slug "${pageSlug}" is already claimed by ${ownership.owner} (content types: ${ownership.contentTypes.join(', ')}). Use a different slug or coordinate with the owning otter.`,
        callerOtter,
        attemptedPath: filePath,
        allowedPaths: [],
      };
      return { text: JSON.stringify(result), isError: true };
    }
  }

  // 6. Write the file
  try {
    if (createDirectories) {
      mkdirSync(dirname(fullPath), { recursive: true });
    }

    writeFileSync(fullPath, content, { encoding: 'utf-8' });

    // Update page registry if this is a page write
    if (pageSlug) {
      try {
        const registry = readPageRegistry(cwd);
        registry[pageSlug] = {
          slug: pageSlug,
          claimedBy: callerOtter,
          contentTypes: extractContentTypes(content),
          writtenAt: new Date().toISOString(),
        };
        writePageRegistry(cwd, registry);
      } catch {
        // Registry update is defense-in-depth — don't fail the write if registry fails
      }
    }

    const result: SafeWriteSuccess = {
      success: true,
      path: normalized,
      bytesWritten: Buffer.byteLength(content, 'utf-8'),
      allowRule: check.rule ?? 'unknown',
    };
    return { text: JSON.stringify(result), isError: false };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    const result: SafeWriteRejection = {
      success: false,
      error: `Write failed: ${message}`,
      callerOtter,
      attemptedPath: filePath,
      allowedPaths: [],
    };
    return { text: JSON.stringify(result), isError: true };
  }
}

/**
 * Public API — wraps _safeWriteInner with best-effort telemetry (swp-im3c).
 * All emit() calls are fire-and-forget; failures never surface to callers.
 */
export function handleSafeWrite(input: SafeWriteInput): { text: string; isError: boolean } {
  const cwd = input._cwd ?? process.cwd();
  const { filePath, callerOtter } = input;
  // Telemetry: best-effort, never blocks (swp-im3c)
  emit(
    { type: 'tool_call', toolName: 'stackwright_pro_safe_write', args: { callerOtter, filePath } },
    { cwd }
  );
  const result = _safeWriteInner(input);
  try {
    const parsed = JSON.parse(result.text) as {
      success?: boolean;
      path?: string;
      bytesWritten?: number;
    };
    if (parsed.success === true && parsed.path) {
      // Telemetry: best-effort, never blocks (swp-im3c)
      emit({ type: 'file_write', path: parsed.path, bytes: parsed.bytesWritten }, { cwd });
    }
    // Telemetry: best-effort, never blocks (swp-im3c)
    emit(
      {
        type: 'tool_complete',
        toolName: 'stackwright_pro_safe_write',
        success: parsed.success === true,
      },
      { cwd }
    );
  } catch {
    // Telemetry parse/emit errors never surface to callers
  }
  return result;
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerSafeWriteTools(server: McpServer): void {
  const DESC =
    'Controlled file-write chokepoint. Every write from specialist otters goes through this tool with per-otter path allowlists. The LLM cannot write to arbitrary filesystem paths.';

  server.tool(
    'stackwright_pro_safe_write',
    DESC,
    {
      callerOtter: z
        .string()
        .describe('The otter agent name requesting the write, e.g. "stackwright-pro-page-otter"'),
      filePath: z
        .string()
        .describe('Relative path from project root, e.g. "pages/dashboard/content.yml"'),
      content: z.string().describe('File content to write'),
      createDirectories: boolCoerce(z.boolean().optional().default(true)).describe(
        "Create parent directories if they don't exist. Default: true"
      ),
    },
    async ({ callerOtter, filePath, content, createDirectories }) => {
      const result = handleSafeWrite({
        callerOtter,
        filePath,
        content,
        ...(createDirectories != null ? { createDirectories } : {}),
      });
      return { content: [{ type: 'text' as const, text: result.text }], isError: result.isError };
    }
  );
}
